import React, { useState } from 'react';
import { ArrowLeft, CalendarClock, Download, Building2, History, ChevronDown, Check, Activity } from 'lucide-react';
import ProposalProgressBar from './Footer/ProposalProgressBar';
import { getActiveStep } from '../utils/helperUtils';
import { STEPS_MANAGER, STEPS_MEMBER } from '../constants/rfpConstants';
import AuditLogsModal from './AuditLogsModal';

/**
 * ClientDecisionControl sub-component
 * Handles Client Acceptance or Rejection and prompts for comments inline.
 */
export const ClientDecisionControl = ({ currentStage, statusCode, executeStageTransition }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [decisionType, setDecisionType] = useState(null); // 'Client Accepted' or 'Client Rejected'
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const containerRef = React.useRef(null);

    // Close dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const stageNormalized = String(currentStage || '').toLowerCase();
    const isAccepted = ['client accepted', 'proposal accepted', 'accepted', '6'].includes(stageNormalized) || Number(statusCode) === 6;
    const isRejected = ['client rejected', 'proposal rejected', 'rejected', 'lost', '7'].includes(stageNormalized) || Number(statusCode) === 7;

    if (isAccepted) {
        return (
            <div className="client-decision-badge accepted">
                Client Accepted
            </div>
        );
    }
    if (isRejected) {
        return (
            <div className="client-decision-badge rejected">
                Client Rejected
            </div>
        );
    }

    const handleSelectOption = (type) => {
        setDecisionType(type);
        setError('');
        setNotes('');
        setIsOpen(false);
        setShowModal(true);
    };

    const handleConfirm = async () => {
        if (!notes || notes.trim().length < 5) {
            setError('Please write at least 5 letters for comments.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const success = await executeStageTransition(decisionType, notes);
            if (success) {
                setShowModal(false);
            } else {
                setError('Failed to update stage. Try again.');
            }
        } catch (err) {
            setError('Error updating stage.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="client-decision-container" ref={containerRef}>
            <button className="client-decision-trigger" onClick={() => setIsOpen(!isOpen)}>
                Proposal Decision
                <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {isOpen && (
                <div className="client-decision-dropdown">
                    <button className="dropdown-opt accepted-opt" onClick={() => handleSelectOption('Client Accepted')}>
                        Client Accepted
                    </button>
                    <button className="dropdown-opt rejected-opt" onClick={() => handleSelectOption('Client Rejected')}>
                        Client Rejected
                    </button>
                </div>
            )}

            {showModal && (
                <div className="decision-overlay" onClick={() => !loading && setShowModal(false)}>
                    <div className="decision-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="decision-modal-header">
                            <h3>Confirm {decisionType === 'Client Accepted' || decisionType === 'Proposal Accepted' ? 'Proposal Acceptance' : 'Proposal Rejection'}</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)} disabled={loading}>✕</button>
                        </div>
                        <div className="decision-modal-body">
                            <p>
                                Are you sure you want to transition this proposal's final status to
                                <strong style={{ color: (decisionType === 'Client Accepted' || decisionType === 'Proposal Accepted') ? '#16A34A' : '#EF4444', marginLeft: '4px' }}>
                                    {(decisionType === 'Client Accepted' || decisionType === 'Proposal Accepted') ? 'Accepted' : 'Rejected'}
                                </strong>?
                                This update will finalise project outcomes.
                            </p>
                            <div className="notes-group">
                                <label>Add Decision Notes / Comments <span className="req-star">*</span></label>
                                <textarea
                                    placeholder={`Enter feedback or reason for ${(decisionType === 'Client Accepted' || decisionType === 'Proposal Accepted') ? 'acceptance' : 'rejection'}...`}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    disabled={loading}
                                />
                                {error && <span className="error-text">{error}</span>}
                            </div>
                        </div>
                        <div className="decision-modal-footer">
                            <button className="cancel-btn" onClick={() => setShowModal(false)} disabled={loading}>Cancel</button>
                            <button
                                className={`confirm-btn ${(decisionType === 'Client Accepted' || decisionType === 'Proposal Accepted') ? 'accept' : 'reject'}`}
                                onClick={handleConfirm}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Confirm Decision'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * SectionStatusDropdown sub-component
 * Renders the vertical section progression stepper dropdown beside the proposal title for Team Members.
 */
const SectionStatusDropdown = ({ selectedSection }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!selectedSection) return null;

    const code = selectedSection.status_code ||
        (selectedSection.status && typeof selectedSection.status === 'object' ? selectedSection.status.code : null) ||
        parseInt(selectedSection.status) || 51;

    let text = 'Assigned';
    if (code === 51) text = 'Assigned';
    else if (code === 52) text = 'In-Progress';
    else if (code === 53) text = 'Submitted';
    else if (code === 54) text = 'Approved';
    else if (code === 55) text = 'Revision';

    let displaySteps = [];
    if (code === 55) {
        displaySteps = [
            { id: 51, label: 'Assigned' },
            { id: 52, label: 'In-Progress' },
            { id: 53, label: 'Submitted' },
            { id: 55, label: 'Revision' }
        ];
    } else {
        const allSteps = [
            { id: 51, label: 'Assigned' },
            { id: 52, label: 'In-Progress' },
            { id: 53, label: 'Submitted' },
            { id: 54, label: 'Approved' }
        ];
        displaySteps = allSteps.filter(s => s.id <= code);
    }

    return (
        <div ref={containerRef} style={{ position: 'relative', marginLeft: '16px', display: 'inline-flex', alignItems: 'center', zIndex: 90 }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    padding: '0.5rem 0.8rem',
                    background: '#FFF1EC',
                    border: '1px solid #FFE1D6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <Activity size={14} style={{ color: '#E9471D' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#E9471D', whiteSpace: 'nowrap' }}>Section: {text}</span>
                <ChevronDown size={14} style={{ color: '#E9471D', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '260px',
                    background: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                    padding: '1.25rem',
                    marginTop: '8px',
                    zIndex: 100,
                    display: 'block'
                }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1.25rem', paddingLeft: '0.25rem' }}>
                        Section Progression
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        {displaySteps.map((step, index) => {
                            const isCompleted = step.id !== code;
                            const isActive = step.id === code;

                            return (
                                <div key={step.id} style={{ display: 'flex', gap: '1.6rem', minHeight: '50px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '24px' }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            background: isActive ? '#FF5526' : '#10B981',
                                            border: `2px solid ${isActive ? '#FF5526' : '#10B981'}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.7rem',
                                            fontWeight: '700',
                                            color: 'white',
                                            zIndex: 2,
                                            boxShadow: isActive ? '0 0 10px rgba(240, 74, 35, 0.3)' : 'none'
                                        }}>
                                            {isCompleted ? <Check size={12} strokeWidth={3} /> : (index + 1)}
                                        </div>
                                        {index < displaySteps.length - 1 && (
                                            <div style={{
                                                width: '2px',
                                                flex: 1,
                                                background: '#10B981',
                                                margin: '2px 0'
                                            }} />
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '0.5rem' }}>
                                        <span style={{
                                            fontSize: '0.875rem',
                                            fontWeight: isActive ? '700' : '600',
                                            color: isActive ? '#FF5526' : '#1E293B'
                                        }}>
                                            {step.label}
                                        </span>
                                        <span style={{
                                            fontSize: '0.7rem',
                                            fontWeight: '500',
                                            color: isActive ? '#FF5526' : '#94A3B8',
                                            opacity: isActive ? 0.8 : 1
                                        }}>
                                            {isActive ? 'Current Phase' : 'Completed'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * RFPAnalysisHeader — extracted from RFPAnalysisResult.jsx (Phase 6 refactor).
 * Renders the top stepper bar: Back button, Progress Bar, Key Info & Download actions.
 */
const RFPAnalysisHeader = ({
    isManager,
    editableData,
    navigate,
    setIsKeyInfoModalOpen,
    setShowExport,
    rfpId,
    fetchOverview,
    executeStageTransition,
    isStageUpdating,
    isNoGo = false,
    isTeamLeadViewOnly = false,
    selectedSection = null,
    handleSubmitToLead = null,
    fetchRFPData = null
}) => {
    const [showAuditLogs, setShowAuditLogs] = useState(false);

    const isSubmitted = String(editableData?.global_status?.label || editableData?.proposal_stage || '').toLowerCase().includes('submitted') || 
                        editableData?.global_status?.code === 5 || 
                        getActiveStep(editableData, isManager) === 4;

    const isSectionAutoClosed = () => {
        if (!selectedSection) return false;
        const code = selectedSection.status_code ||
            (selectedSection.status && typeof selectedSection.status === 'object' ? selectedSection.status.code : null) ||
            parseInt(selectedSection.status) || 0;
        
        const label = String(
            selectedSection.status?.label || 
            selectedSection.status?.name || 
            selectedSection.status || 
            ''
        ).toLowerCase();

        return code === 57 || label.includes('auto-closed') || label.includes('autoclosed') || label.includes('auto closed');
    };

    const isSectionSessionExpired = () => {
        if (!selectedSection) return false;
        const code = selectedSection.status_code ||
            (selectedSection.status && typeof selectedSection.status === 'object' ? selectedSection.status.code : null) ||
            parseInt(selectedSection.status) || 0;
        
        const label = String(
            selectedSection.status?.label || 
            selectedSection.status?.name || 
            selectedSection.status || 
            ''
        ).toLowerCase();

        return code === 56 || label.includes('expired');
    };

    const isSectionClosed = () => {
        return isSectionAutoClosed() || isSectionSessionExpired();
    };

    const getClosedSectionInfo = () => {
        if (isSectionSessionExpired()) {
            return {
                label: 'Session Expired',
                color: '#E9471D',
                bg: '#FFF1EC',
                border: '#FFD8A8'
            };
        } else {
            return {
                label: 'Section: Auto-closed',
                color: '#64748B',
                bg: '#F1F5F9',
                border: '#CBD5E1'
            };
        }
    };

    const getSectionActiveStep = () => {
        if (!selectedSection) return 51;
        return selectedSection.status_code ||
            (selectedSection.status && typeof selectedSection.status === 'object' ? selectedSection.status.code : null) ||
            parseInt(selectedSection.status) || 51;
    };

    const getSectionStatusLabel = () => {
        if (isSectionSessionExpired()) return 'Session Expired';
        if (isSectionAutoClosed()) return 'Auto-closed';
        const code = getSectionActiveStep();
        if (code === 51) return 'Assigned';
        if (code === 52) return 'In-Progress';
        if (code === 53) return 'Submitted';
        if (code === 54) return 'Approved';
        if (code === 55) return 'Revision';
        return 'Assigned';
    };

    const getSectionSteps = () => {
        if (!selectedSection) return [];
        const code = getSectionActiveStep();

        if (code === 55) {
            return [
                { id: 51, label: 'Assigned' },
                { id: 52, label: 'In-Progress' },
                { id: 53, label: 'Submitted' },
                { id: 55, label: 'Revision' }
            ];
        }
        return [
            { id: 51, label: 'Assigned' },
            { id: 52, label: 'In-Progress' },
            { id: 53, label: 'Submitted' },
            { id: 54, label: 'Approved' }
        ];
    };

    return (
        <div className="stepper-section">
            <div className="header-actions-left">
                <button
                    className="back-to-dashboard"
                    onClick={() => navigate(isManager ? '/proposals' : '/my-proposals')}
                >
                    <ArrowLeft size={18} />
                </button>

                {/* Team Member Header: Beside the back arrow */}
                {!isManager && (
                    <div className="member-header-info" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.85rem',
                        marginLeft: '1rem',
                        padding: '4px 0'
                    }}>
                        <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '4px',
                            background: '#FFF1EC',
                            color: '#FF5526',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <Building2 size={20} />
                        </div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            lineHeight: '1.2'
                        }}>
                            <div style={{
                                fontSize: '1.2rem',
                                fontWeight: '700',
                                color: '#434B51',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '600px',
                                display: 'flex',
                                alignItems: 'center'
                             }}>
                                <span>{editableData.PROPOSAL_TITLE || editableData.proposal_title || 'Untitled Proposal'}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="header-actions-right" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                {/* Show Progress Bar for both Lead and Member */}
                {!isManager && selectedSection ? (
                    isSectionClosed() ? (
                        (() => {
                            const info = getClosedSectionInfo();
                            return (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.6rem',
                                    padding: '0.5rem 0.8rem',
                                    background: info.bg,
                                    border: `1px solid ${info.border}`,
                                    borderRadius: '6px',
                                    color: info.color,
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    userSelect: 'none'
                                }}>
                                    <Activity size={14} style={{ color: info.color }} />
                                    <span>{info.label}</span>
                                </div>
                            );
                        })()
                    ) : (
                        <ProposalProgressBar
                            activeStep={getSectionActiveStep()}
                            currentStageLabel={`Section: ${getSectionStatusLabel()}`}
                            steps={getSectionSteps()}
                            rfpId={rfpId}
                            fetchOverview={fetchOverview}
                            fetchRFPData={fetchRFPData}
                            isManager={isManager}
                            onStepChange={async (step) => {
                                if (step.id === 53) {
                                    // Trigger submission to Lead
                                    if (handleSubmitToLead) {
                                        await handleSubmitToLead(selectedSection.label, fetchRFPData);
                                    }
                                }
                            }}
                        />
                    )
                ) : (
                    <>
                        <ProposalProgressBar
                            activeStep={getActiveStep(editableData, isManager)}
                            currentStageLabel={editableData?.global_status?.label || editableData?.proposal_stage}
                            steps={isManager ? STEPS_MANAGER : STEPS_MEMBER}
                            rfpId={rfpId}
                            fetchOverview={fetchOverview}
                            fetchRFPData={fetchRFPData}
                            isManager={isManager}
                            enableSubmittedClick={true}
                        />
                        {!isNoGo && executeStageTransition && isSubmitted && isManager && (
                            <ClientDecisionControl
                                currentStage={editableData?.global_status?.label || editableData?.proposal_stage}
                                statusCode={editableData?.global_status?.code}
                                executeStageTransition={executeStageTransition}
                            />
                        )}
                    </>
                )}

                {!isNoGo && isManager && (
                    <button
                        className="update-key-info-btn header-minor-btn"
                        onClick={() => !isTeamLeadViewOnly && setIsKeyInfoModalOpen(true)}
                        disabled={isTeamLeadViewOnly}
                        style={{
                            opacity: isTeamLeadViewOnly ? 0.5 : 1,
                            cursor: isTeamLeadViewOnly ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <CalendarClock size={14} />
                        <span>Update Key Information</span>
                    </button>
                )}
                {isManager && (
                    <button className="export-action-btn" onClick={() => setShowExport(true)}>
                        <Download size={16} />
                        <span>Download</span>
                    </button>
                )}
                {isManager && (
                    <button className="export-action-btn" onClick={() => setShowAuditLogs(true)}>
                        <History size={16} />
                        <span>Audit Logs</span>
                    </button>
                )}
            </div>

            {/* Audit Logs Modal */}
            {showAuditLogs && (
                <AuditLogsModal
                    rfpId={rfpId}
                    onClose={() => setShowAuditLogs(false)}
                />
            )}
        </div>
    );
};

export default RFPAnalysisHeader;
